// TriggerWatch Deployments page — MSP portal
// Customers create deployments targeting devices/models with a chosen package, and monitor results

import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import config from "../config";
import Navbar from "../components/Navbar";
import useSuspended from "../hooks/useSuspended";

const API_URL = config.API_URL;

export default function TriggerWatchDeployments() {
  const navigate = useNavigate();
  const isSuspended = useSuspended();
  const [searchParams] = useSearchParams();
  const preselectedPackageId = searchParams.get("package_id");
  const token = sessionStorage.getItem("token");
  const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  const ORG_ID = storedUser.organization_id;

  const [deployments, setDeployments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [packageId, setPackageId] = useState("");
  const [targetType, setTargetType] = useState("single_device");
  const [targetDeviceId, setTargetDeviceId] = useState("");
  const [targetModel, setTargetModel] = useState("");
  const [scheduleNow, setScheduleNow] = useState(true);
  const [scheduledAt, setScheduledAt] = useState("");

  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    fetchDeployments();
    fetchPackages();
    fetchDevices();
  }, []);

  async function fetchDeployments() {
    try {
      const res = await axios.get(`${API_URL}/organizations/${ORG_ID}/tw-deployments`, authHeaders);
      setDeployments(res.data);
    } catch (err) {
      console.error("Failed to load deployments", err);
    } finally {
      setLoading(false);
    }
  }

  async function fetchPackages() {
    try {
      const res = await axios.get(`${API_URL}/organizations/${ORG_ID}/tw-packages`, authHeaders);
      setPackages(res.data);
      // If we arrived here via a "Deploy" button on the Packages page, pre-select that package
      if (preselectedPackageId && res.data.some(p => p.id === preselectedPackageId)) {
        setPackageId(preselectedPackageId);
      } else if (res.data.length > 0) {
        setPackageId(res.data[0].id);
      }
    } catch (err) {
      console.error("Failed to load packages", err);
    }
  }

  async function fetchDevices() {
    try {
      const res = await axios.get(`${API_URL}/organizations/${ORG_ID}/devices`, authHeaders);
      setDevices(res.data);
      if (res.data.length > 0) setTargetDeviceId(res.data[0].id);
    } catch (err) {
      console.error("Failed to load devices", err);
    }
  }

  // Unique manufacturer+model combos, derived from the device list, for the "by model" target option
  const uniqueModels = [...new Set(devices.map(d => d.model).filter(Boolean))];

function resetForm() {
    setPackageId(packages.length > 0 ? packages[0].id : "");
    setTargetType("single_device");
    setTargetDeviceId(devices.length > 0 ? devices[0].id : "");
    setTargetModel("");
    setScheduleNow(true);
    setScheduledAt("");
    setSuccessMsg("");
    setErrorMsg("");
  }

  async function handleSubmit() {
    if (!packageId) {
      setErrorMsg("Please select a package.");
      return;
    }
    if (targetType === "single_device" && !targetDeviceId) {
      setErrorMsg("Please select a target device.");
      return;
    }
    if (targetType === "by_model" && !targetModel) {
      setErrorMsg("Please select a target model.");
      return;
    }
    if (!scheduleNow && !scheduledAt) {
      setErrorMsg("Please pick a date/time, or choose to deploy now.");
      return;
    }

    setSubmitting(true);
    setErrorMsg("");
    setSuccessMsg("");

    const params = new URLSearchParams({
      package_id: packageId,
      target_type: targetType
    });
    if (targetType === "single_device") params.append("target_device_id", targetDeviceId);
    if (targetType === "by_model") params.append("target_model", targetModel);
    if (!scheduleNow && scheduledAt) params.append("scheduled_at", scheduledAt);

    try {
      const res = await axios.post(
        `${API_URL}/organizations/${ORG_ID}/tw-deployments?${params.toString()}`,
        {},
        authHeaders
      );
      setSuccessMsg(`✅ Deployment created — targeting ${res.data.devices_targeted} device(s)`);
      fetchDeployments();
    } catch (err) {
      setErrorMsg(`❌ ${err.response?.data?.detail || "Failed to create deployment."}`);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel(depId, e) {
    e.stopPropagation(); // don't trigger the row's expand/collapse click
    if (!window.confirm("Cancel this deployment? Devices that haven't started yet will be stopped. Devices already running will still finish.")) return;
    try {
      await axios.post(`${API_URL}/organizations/${ORG_ID}/tw-deployments/${depId}/cancel`, {}, authHeaders);
      fetchDeployments();
      if (expandedId === depId) {
        const res = await axios.get(`${API_URL}/organizations/${ORG_ID}/tw-deployments/${depId}`, authHeaders);
        setExpandedDetail(res.data);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to cancel deployment.");
    }
  }

  async function toggleExpand(depId) {
    if (expandedId === depId) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(depId);
    try {
      const res = await axios.get(`${API_URL}/organizations/${ORG_ID}/tw-deployments/${depId}`, authHeaders);
      setExpandedDetail(res.data);
    } catch (err) {
      console.error("Failed to load deployment detail", err);
    }
  }

  function statusBadge(status) {
    if (status === "completed") return "bg-green-900 text-green-300";
    if (status === "partial") return "bg-yellow-900 text-yellow-300";
    if (status === "failed") return "bg-red-900 text-red-300";
    if (status === "in_progress") return "bg-blue-900 text-blue-300";
    if (status === "cancelled") return "bg-pink-900 text-pink-300";
    return "bg-gray-700 text-gray-400";
  }

  function deviceStatusColor(status) {
    if (status === "success") return "text-green-400";
    if (status === "failed") return "text-red-400";
    if (status === "in_progress") return "text-blue-400";
    if (status === "cancelled") return "text-pink-400";
    return "text-gray-500";
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Navbar orgName={storedUser.organization_name} />

      <div className="max-w-4xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-bold text-white mb-1">TriggerWatch Deployments</h1>
        <p className="text-gray-400 text-sm mb-2">
          Push packages out to your devices and monitor results.
        </p>
        <p className="text-gray-600 text-xs mb-8">
          Deployment history is kept for 7 days.
        </p>

        {/* Create form */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-10">
          <h2 className="text-lg font-semibold text-white mb-4">Create a Deployment</h2>

          {packages.length === 0 ? (
            <p className="text-gray-500 text-sm mb-4">
              No packages available. Create one on the{" "}
              <button onClick={() => navigate("/triggerwatch/packages")} className="text-blue-400 hover:text-blue-300 underline">
                Packages page
              </button>{" "}
              first.
            </p>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-1">Package</label>
                <select
                  value={packageId}
                  onChange={e => setPackageId(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                >
                  {packages.map(p => (
                    <option key={p.id} value={p.id}>{p.name} v{p.version} ({p.payload_type})</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-1">Target</label>
                <select
                  value={targetType}
                  onChange={e => setTargetType(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                >
                  <option value="single_device">Single Device</option>
                  <option value="by_model">All Devices of a Model</option>
                  <option value="all_devices">All Devices in Organization</option>
                </select>
              </div>

              {targetType === "single_device" && (
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-1">Device</label>
                  <select
                    value={targetDeviceId}
                    onChange={e => setTargetDeviceId(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    {devices.map(d => (
                      <option key={d.id} value={d.id}>{d.hostname} — {d.manufacturer} {d.model}</option>
                    ))}
                  </select>
                </div>
              )}

              {targetType === "by_model" && (
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-1">Model</label>
                  <select
                    value={targetModel}
                    onChange={e => setTargetModel(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="">Select a model</option>
                    {uniqueModels.map(m => (
                      <option key={m} value={m}>{m}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="mb-4">
                <label className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                  <input
                    type="checkbox"
                    checked={scheduleNow}
                    onChange={e => setScheduleNow(e.target.checked)}
                  />
                  Deploy immediately
                </label>
                {!scheduleNow && (
                  <input
                    type="datetime-local"
                    value={scheduledAt}
                    onChange={e => setScheduledAt(e.target.value)}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white text-sm focus:outline-none focus:border-blue-500"
                  />
                )}
              </div>

              {successMsg && <p className="text-green-400 text-sm mb-3">{successMsg}</p>}
              {errorMsg && <p className="text-red-400 text-sm mb-3">{errorMsg}</p>}

              <div className="flex items-center gap-3">
                <button
                  onClick={handleSubmit}
                  disabled={submitting || isSuspended}
                  className="bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
                >
                  {submitting ? "Creating..." : "Create Deployment"}
                </button>
                <button
                  onClick={() => navigate(-1)}
                  className="bg-transparent border border-gray-700 hover:border-gray-500 text-gray-400 hover:text-white text-sm font-medium px-5 py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>

        {/* Deployment list */}
        <h2 className="text-lg font-semibold text-white mb-4">Recent Deployments</h2>

        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : deployments.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
            No deployments yet.
          </div>
        ) : (
          <div className="space-y-4">
            {deployments.map(dep => (
              <div key={dep.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div
                  className="flex items-start justify-between gap-4 cursor-pointer"
                  onClick={() => toggleExpand(dep.id)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-white font-medium text-sm">{dep.package_name}</h3>
                      <span className="text-gray-500 text-xs">v{dep.package_version}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge(dep.status)}`}>
                        {dep.status}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm mb-1">
                      {dep.target_type === "single_device" && "Single device"}
                      {dep.target_type === "by_model" && `Model: ${dep.target_model}`}
                      {dep.target_type === "all_devices" && "All devices in organization"}
                      {" · "}
                      {dep.succeeded}/{dep.total_devices} succeeded
                      {dep.failed > 0 && `, ${dep.failed} failed`}
                      {dep.pending > 0 && `, ${dep.pending} pending`}
                    </p>
                    <p className="text-gray-600 text-xs">
                      Created by {dep.created_by} ·{" "}
                      {new Date(dep.created_at).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 whitespace-nowrap">
                    {(dep.status === "pending" || dep.status === "in_progress") && (
                      <button
                        onClick={(e) => handleCancel(dep.id, e)}
                        disabled={isSuspended}
                        className={`text-xs font-medium transition-colors ${isSuspended ? "text-gray-600 cursor-not-allowed" : "text-red-400 hover:text-red-300"}`}
                      >
                        Cancel
                      </button>
                    )}
                    <span className="text-gray-500 text-xs">
                      {expandedId === dep.id ? "▲ Hide" : "▼ Details"}
                    </span>
                  </div>
                </div>

                {expandedId === dep.id && expandedDetail && (
                  <div className="mt-4 pt-4 border-t border-gray-800 space-y-3">
                    {expandedDetail.devices.map(d => (
                      <div key={d.device_id} className="bg-gray-800 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-white text-sm font-medium">{d.hostname}</span>
                          <span className={`text-xs font-medium ${deviceStatusColor(d.status)}`}>
                            {d.status}
                            {d.exit_code !== null && ` (exit ${d.exit_code})`}
                          </span>
                        </div>
                        {d.output && (
                          <pre className="text-gray-400 text-xs whitespace-pre-wrap bg-gray-900 rounded p-2 mt-2">{d.output}</pre>
                        )}
                        {d.error_message && (
                          <p className="text-red-400 text-xs mt-2">{d.error_message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}